import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from '../../i18n/useTranslation';
import { useWalletStore } from '../../store/walletStore';
import { useDockDrag } from './useDockDrag';
const backpackIcon = '/icons/backpack.svg';
const backpackOpenIcon = '/icons/backpack_open.svg';
const backpackEmptyIcon = '/icons/backpack_empty.svg';
import styles from './InventorySection.module.css';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

interface ItemDef {
  id: string;
  name: string;
  slot: string;
  icon_url?: string;
  description?: string;
  min_level?: number;
  item_type?: string;
  energyRestorePercentage?: number | null;
}

interface InventoryEntry {
  item: ItemDef;
  quantity: number;
}

interface InventoryResponse {
  inventory: InventoryEntry[];
  equipment: Record<string, ItemDef>;
  kamas: number;
  /** Saldo "Ta Feito" — a moeda real (barra do topo / Forja / Mercador). */
  walletBalance: number;
  capacity: number;
}

interface InventorySectionProps {
  token: string;
  /** Ícone pequeno (só imagem), para a barra compacta do Hub. */
  compact?: boolean;
  /** Janela "encaixada" abaixo do painel da cidade (Hub) — sem escurecer o fundo. */
  dock?: boolean;
  dockTop?: number;
  /**
   * Modo CONTROLADO pelo pai (Hub): não renderiza botão próprio; a janela
   * abre/fecha por estas props. Usado para garantir 1 janela por vez.
   */
  controlled?: { open: boolean; onClose: () => void };
}

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────

export default function InventorySection({ token, compact = false, dock = false, dockTop = 200, controlled }: InventorySectionProps) {
  const { t } = useTranslation();
  const [isOpenLocal, setIsOpen]      = useState(false);
  const isOpen = controlled ? controlled.open : isOpenLocal;
  const dragCtl = useDockDrag(isOpen);
  const [isHovered, setIsHovered]     = useState(false);
  const [isAnimOpen, setIsAnimOpen]   = useState(false);
  const [data, setData]               = useState<InventoryResponse | null>(null);
  const [loading, setLoading]         = useState(false);
  const [error, setError]             = useState<string | null>(null);
  const [selectedItem, setSelectedItem] = useState<InventoryEntry | null>(null);
  const [usingId, setUsingId] = useState<string | null>(null);
  /** Ícones que falharam ao carregar → cai no placeholder (inicial do nome). */
  const [brokenIcons, setBrokenIcons] = useState<Set<string>>(new Set());
  const markBroken = (url: string) =>
    setBrokenIcons(prev => (prev.has(url) ? prev : new Set(prev).add(url)));
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const storeBalance = useWalletStore(s => s.balance);
  const setBalanceStore = useWalletStore(s => s.setBalance);
  const inventoryNonce = useWalletStore(s => s.inventoryNonce);
  const bumpInventory = useWalletStore(s => s.bumpInventory);

  // Usar um consumível (poção etc.) — restaura vida/energia via
  // /api/user/equipment/use. Dispara 'energy-changed' pra barra de energia
  // do dashboard / Hub atualizar na hora.
  const useConsumable = async (itemId: string) => {
    setUsingId(itemId);
    try {
      const res = await fetch('/api/user/equipment/use', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ item_id: itemId }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error ?? `HTTP ${res.status}`);
      if (typeof body.current_energy === 'number') {
        window.dispatchEvent(new CustomEvent('energy-changed', { detail: body.current_energy }));
      }
      bumpInventory();
      await fetchInventory(true);
      setSelectedItem(null);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setUsingId(null);
    }
  };

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') handleClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const fetchInventory = async (force = false) => {
    if (data && !force) return;
    try {
      setLoading(true);
      setError(null);
      const res = await fetch('/api/user/inventory', {
        headers: { Authorization: `Bearer ${token}` },
        cache: 'no-store',
      });
      if (!res.ok) throw new Error(t('inventory.error_load'));
      const json = await res.json();
      setData(json);
      if (typeof json.walletBalance === 'number') setBalanceStore(json.walletBalance);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInventory();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Recarrega quando qualquer transação (compra/venda/forja) altera a mochila.
  useEffect(() => {
    if (inventoryNonce === 0) return;
    fetchInventory(true);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inventoryNonce]);

  const handleOpen = (e?: React.MouseEvent) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setIsOpen(true);
    setIsAnimOpen(true);
    fetchInventory();
  };

  const handleClose = () => {
    if (controlled) { setSelectedItem(null); controlled.onClose(); return; }
    setIsAnimOpen(false);
    setIsHovered(false); // Reseta o hover ao fechar
    closeTimer.current = setTimeout(() => {
      setIsOpen(false);
      setSelectedItem(null);
    }, 220);
  };

  // Modo controlado: carrega os dados ao abrir.
  useEffect(() => {
    if (controlled?.open) fetchInventory();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [controlled?.open]);

  const getItemName = (item: ItemDef): string => {
    const baseId = item.icon_url
      ? item.icon_url.split('/').pop()?.replace(/\?.*$/, '').replace('.png', '')
      : item.id ?? item.name.toLowerCase().replace(/\s+/g, '_');
    const key = `item.${baseId}.name` as any;
    const translated = t(key);
    return translated !== key ? translated : item.name;
  };

  const totalItems = data?.inventory.reduce((s, e) => s + e.quantity, 0) ?? 0;
  // Passar o mouse já mostra os detalhes; o item fica fixo até outro ser
  // apontado (dá pra alcançar o botão Usar). Clique = mesmo efeito (toque).
  const detailItem = selectedItem;
  const iconOf = (item: ItemDef) =>
    item.icon_url && !brokenIcons.has(item.icon_url) ? `${item.icon_url}?v=4` : null;

  return (
    <>
      {controlled ? null : compact ? (
        <button className={styles.compactBtn} onClick={handleOpen} title={t('inventory.title')} aria-label={t('inventory.title')}>
          <img src={totalItems > 0 ? backpackIcon : backpackEmptyIcon} alt="" className={styles.compactImg} />
        </button>
      ) : (
      /* ── Backpack button — parte do card ──────────────────────────────── */
      <button
        className={styles.backpackButton}
        onClick={handleOpen}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        title={t('inventory.title')}
        aria-label={t('inventory.title')}
      >
        {/* Aura glow ring */}
        <div className={`${styles.auraRing} ${isHovered ? styles.auraActive : ''}`} />

        {/* Backpack image — estado baseado nos itens e hover */}
        <div className={styles.imgWrap}>
          <img
            src={
              totalItems > 0
                ? (isHovered && !isOpen ? backpackOpenIcon : backpackIcon)
                : backpackEmptyIcon // Vazia nunca abre, pois não tem itens
            }
            alt=""
            className={styles.backpackImg}
          />
          {/* Particle sparks on hover (apenas se tiver itens e modal fechado) */}
          {isHovered && !isOpen && totalItems > 0 && (
            <>
              <span className={`${styles.spark} ${styles.spark1}`} />
              <span className={`${styles.spark} ${styles.spark2}`} />
              <span className={`${styles.spark} ${styles.spark3}`} />
              <span className={`${styles.spark} ${styles.spark4}`} />
            </>
          )}
        </div>
        <span className={styles.tileLabel}>{t('inventory.title')}</span>

      </button>
      )}

      {/* ── Inventory modal — centred on screen ──────────────────────────── */}
      {isOpen && createPortal(
        <div
          className={dock ? styles.dock : `${styles.overlay} ${isAnimOpen ? styles.overlayIn : styles.overlayOut}`}
          data-dock-window={dock ? '' : undefined}
          onClick={dock ? undefined : handleClose}
          style={dock ? ({ '--dock-top': `${dockTop}px`, ...dragCtl.style } as React.CSSProperties) : undefined}
        >
          <div
            className={dock
              ? `${styles.modal} ${styles.dockModal}`
              : `${styles.modal} ${isAnimOpen ? styles.modalIn : styles.modalOut}`}
            onClick={e => e.stopPropagation()}
            role="dialog"
            aria-modal={dock ? undefined : 'true'}
            aria-label={t('inventory.title')}
          >
            {/* ── Header ── */}
            <header
              className={`${styles.modalHeader} ${dock ? styles.dockDragHandle : ''}`}
              onPointerDown={dock ? dragCtl.onHeaderPointerDown : undefined}
            >
              <div className={styles.headerLeft}>
                <img src={backpackIcon} alt="" className={styles.headerIcon} />
                <h2 className={styles.modalTitle}>{t('inventory.title')}</h2>
              </div>
              <div className={styles.headerRight}>
                {data && (
                  <>
                    <span className={styles.kamasChip} style={{ marginRight: '8px' }}>
                      {totalItems} / {data.capacity} itens
                    </span>
                    <span className={styles.kamasChip}>
                      💰 {(storeBalance ?? data.walletBalance).toLocaleString()} TF
                    </span>
                  </>
                )}
                <button className={styles.closeBtn} onClick={handleClose} aria-label="Fechar">✕</button>
              </div>
            </header>

            {/* ── Body ── */}
            <div className={styles.modalBody}>
              {loading && (
                <div className={styles.centerState}>
                  <div className={styles.spinner} />
                  <span>{t('common.loading')}</span>
                </div>
              )}

              {error && (
                <div className={styles.errorBox}>{error}</div>
              )}

              {!loading && !error && data && (
                data.inventory.length === 0 ? (
                  <div className={styles.emptyState}>
                    <span className={styles.emptyGlyph}>🎒</span>
                    <p className={styles.emptyText}>{t('inventory.empty')}</p>
                  </div>
                ) : (
                  <>
                    <div className={styles.itemGrid}>
                      {data.inventory.map((entry, idx) => {
                        const name     = getItemName(entry.item);
                        const iconSrc  = iconOf(entry.item);
                        const selected = selectedItem?.item.id === entry.item.id;

                        return (
                          <button
                            key={idx}
                            className={`${styles.itemSlot} ${selected ? styles.itemSlotActive : ''}`}
                            onClick={() => setSelectedItem(entry)}
                            onMouseEnter={() => setSelectedItem(entry)}
                            title={name}
                          >
                            <div className={styles.slotIconWrap}>
                              {iconSrc ? (
                                <img
                                  src={iconSrc}
                                  alt={name}
                                  className={styles.slotIcon}
                                  onError={() => markBroken(entry.item.icon_url!)}
                                />
                              ) : (
                                <div className={styles.slotPlaceholder}>
                                  {name.charAt(0).toUpperCase()}
                                </div>
                              )}
                              {entry.quantity > 1 && (
                                <span className={styles.slotQty}>×{entry.quantity}</span>
                              )}
                            </div>
                          </button>
                        );
                      })}
                    </div>

                    {/* Item detail */}
                    {detailItem && (
                      <div className={styles.detail}>
                        <div className={styles.detailIcon}>
                          {iconOf(detailItem.item) ? (
                            <img
                              src={iconOf(detailItem.item)!}
                              alt={getItemName(detailItem.item)}
                              className={styles.detailImg}
                              onError={() => markBroken(detailItem.item.icon_url!)}
                            />
                          ) : (
                            <div className={styles.detailPlaceholder}>
                              {getItemName(detailItem.item).charAt(0)}
                            </div>
                          )}
                        </div>
                        <div className={styles.detailInfo}>
                          <h3 className={styles.detailName}>{getItemName(detailItem.item)}</h3>
                          <span className={styles.detailSlot}>{detailItem.item.slot}</span>
                          {detailItem.item.description && (
                            <p className={styles.detailDesc}>{detailItem.item.description}</p>
                          )}
                          <div className={styles.detailMeta}>
                            <span>Nível: {detailItem.item.min_level ?? 1}</span>
                            <span>Qtd: ×{detailItem.quantity}</span>
                          </div>
                          {(detailItem.item.item_type === 'consumable' || detailItem.item.slot === 'Consumable') && (
                            <button
                              className={styles.useBtn}
                              disabled={usingId === detailItem.item.id}
                              onClick={() => useConsumable(detailItem.item.id)}
                            >
                              {usingId === detailItem.item.id
                                ? '…'
                                : `⚡ ${t('inventory.use' as any) !== 'inventory.use' ? t('inventory.use' as any) : 'Usar'}`}
                            </button>
                          )}
                        </div>
                      </div>
                    )}
                  </>
                )
              )}
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
